import type { LocationVisibility } from '~/models'
import { STATE_LABELS } from '~/utils'

export type LocationData = {
  city: string
  stateAbbr: string
  postalCode: string
  county: string
  latitude: number
  longitude: number
}

export type LocationBounds = [[number, number], [number, number]]

export function isGoogleMapsAvailable(): boolean {
  return !!(window.google && window.google.maps)
}

function getGeocoderQuery({
  city,
  county,
  locationVisibility,
  stateAbbr,
}: {
  city?: string
  county?: string
  locationVisibility?: LocationVisibility
  stateAbbr: string
}) {
  const visibility = locationVisibility ?? 'city'

  if (visibility === 'city' && city) {
    return `${city}, ${stateAbbr}, USA`
  } else if (visibility === 'county' && county) {
    const countyLabel = county.includes('Parish') ? county : `${county} County`
    return `${countyLabel}, ${stateAbbr}, USA`
  }

  return `${STATE_LABELS[stateAbbr] ?? stateAbbr}, USA`
}

function getGeocoderRestrictions({
  city,
  county,
  locationVisibility,
  stateAbbr,
}: {
  city?: string
  county?: string
  stateAbbr: string
  locationVisibility?: LocationVisibility
}) {
  const componentRestrictions: google.maps.GeocoderComponentRestrictions = {
    country: 'US',
  }

  if (
    locationVisibility &&
    locationVisibility !== 'state' &&
    (county || city)
  ) {
    componentRestrictions.administrativeArea = stateAbbr
  }

  if (!locationVisibility || locationVisibility === 'city') {
    componentRestrictions.locality = city
  }

  return componentRestrictions
}

async function makeGeocoderRequest(
  request: google.maps.GeocoderRequest
): Promise<google.maps.GeocoderResult[] | null> {
  return new Promise((resolve) => {
    if (!isGoogleMapsAvailable()) {
      resolve(null)

      return
    }

    const geocoder = new window.google.maps.Geocoder()

    geocoder.geocode(request, (results, status) => {
      if (
        status === window.google.maps.GeocoderStatus.OK &&
        results &&
        Array.isArray(results) &&
        results[0]
      ) {
        resolve(results)
      } else {
        resolve(null)
      }
    })
  })
}

function findCityResult(
  results: google.maps.GeocoderResult[] | null
): google.maps.GeocoderResult | undefined {
  if (!results) return undefined

  return results.find((result) => {
    return (
      result.types.includes('locality') ||
      result.types.includes('sublocality') ||
      result.types.includes('administrative_area_level_3')
    )
  })
}

function findCountyResult(
  results: google.maps.GeocoderResult[] | null
): google.maps.GeocoderResult | undefined {
  if (!results) return undefined

  return results.find((result) => {
    return result.types.includes('administrative_area_level_2')
  })
}

function findResult(
  results: google.maps.GeocoderResult[],
  location?: 'city' | 'county'
): google.maps.GeocoderResult {
  if (location === 'city') {
    return findCityResult(results) ?? results[0]
  } else if (location === 'county') {
    return findCountyResult(results) ?? results[0]
  }

  return results[0]
}

function getResultLocation(result: google.maps.GeocoderResult) {
  if (result.geometry?.location) {
    return {
      latitude: result.geometry.location.lat(),
      longitude: result.geometry.location.lng(),
    }
  }

  return null
}

function getResultType({
  city,
  county,
  locationVisibility,
}: {
  city?: string
  county?: string
  locationVisibility?: LocationVisibility
}) {
  if (city && (locationVisibility === 'city' || !locationVisibility)) {
    return 'city'
  } else if (county && locationVisibility === 'county') {
    return 'county'
  } else {
    return undefined
  }
}

export async function useCentroidLocation({
  city,
  county,
  locationVisibility,
  stateAbbr,
}: {
  city?: string
  county?: string
  locationVisibility?: LocationVisibility
  stateAbbr: string
}) {
  const request: google.maps.GeocoderRequest = {
    address: getGeocoderQuery({
      city,
      county,
      locationVisibility,
      stateAbbr,
    }),
    componentRestrictions: getGeocoderRestrictions({
      city,
      county,
      stateAbbr,
      locationVisibility,
    }),
  }

  const results = await makeGeocoderRequest(request)

  if (!results || results.length === 0) return null

  const resultType = getResultType({
    city,
    county,
    locationVisibility,
  })

  const result = findResult(results, resultType)

  return getResultLocation(result)
}
